import React from "react";
import _ from "lodash";

import ACLStore from "lib/aclStore";
import ACLStorage from "lib/aclService";

import { getMemoizedFunction } from "Utils";
import {
  getGroupOrAssessmentTypes,
  RESOURCES_KEY,
} from "CombinedLibrary/modules/CommonUtils";
import { getIsGradingPeriodUnlocked } from "GradingSetup/modules/Utils";
import { getIsProSchool } from "UnitPlans/modules/Utils";

import {
  CURRICULUM_TYPE_PYP,
  ASSESSMENT_TYPE_AI_TUTOR,
} from "Constants/stringConstants";
import {
  SNPv2_ACCESS_PERMISSION,
  ENABLE_ASSESSMENTS_IN_PYP,
} from "Constants/permissionConstants";

import {
  PencilLineOutlined,
  DeleteOutlined,
  PrintOutlined,
  // StudentPendingOutlined,
  TagOutlined,
  ReloadArrowOutlined,
  CopyOutlined,
  LockFilled,
} from "@toddle-edu/ds-icons";

const LEARNING_COURSE_FLOW_ITEM_CATEGORIES = ["FILE", "ASSESSMENT"];

export const PUSHER_EVENTS = {
  FEED_UPDATE: "courseFlowFeedUpdate",
  RESOURCE_UPDATE: "courseFlowResourceUpdate",
};

const ALL_ATTACHMENT_TYPES = [
  "VIDEO",
  "IMAGE",
  "FILE",
  "AUDIO",
  "WORKBOOK",
  "VOICE_NOTE",
  "QUESTION",
  "QUESTION_SUBMISSION",
  "NOTE",
  "LINK",
];

const getCombinedLibraryFeedVariables = ({
  unitPlanId,
  filters,
  learningCourseId,
  first = 20,
}) => {
  const combinedFeedFilters = _.mapValues(filters, (value, key) => {
    const isAllOption = _.isEqual(value, "ALL") && key != "searchText";

    return isAllOption ? [] : value;
  });

  const { selectedItemTypes = [], searchText = "" } = combinedFeedFilters;

  const selectedAssessmentTypes = _.filter(
    selectedItemTypes,
    type => !_.isEqual(type, RESOURCES_KEY)
  );
  const hasResource = _.find(selectedItemTypes, type => {
    return _.isEqual(type, RESOURCES_KEY);
  });

  const { assessmentTypes = [], groupTypes = [] } = getGroupOrAssessmentTypes({
    selectedAssessmentTypes,
  });

  const assessmentFilters = {
    assessmentType: assessmentTypes,

    groupTypes,
  };

  const itemTypes = _.isEmpty(selectedItemTypes)
    ? LEARNING_COURSE_FLOW_ITEM_CATEGORIES
    : [];

  let attachmentTypes = [];

  if (hasResource) {
    itemTypes.push("FILE");
    attachmentTypes = ALL_ATTACHMENT_TYPES;
  }

  if (!_.isEmpty(selectedAssessmentTypes)) {
    itemTypes.push("ASSESSMENT");
  }

  return {
    parentId: learningCourseId,

    filters: {
      searchText,
      unitPlanId,
      itemTypes,
      attachmentTypes,
      assessmentFilters,
      fetchRepeatedResources: false,
      isHierarchicalFeed: false,
    },
    first,
    orderBy: "DISPLAY_SEQUENCE",
  };
};

export const getCombinedLibraryFeedVariablesMemoized = getMemoizedFunction(
  getCombinedLibraryFeedVariables
);

export const getCombinedLibraryFeedVariablesFromState = () => {
  return (dispatch, getState) => {
    const state = getState();

    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );

    return getCombinedLibraryFeedVariablesMemoized({
      unitPlanId,
      filters: combinedLibraryFilters,
      learningCourseId,
    });
  };
};

/**
 * This function receives data in old format of class flow and returns it in new
 * unit flow revamped format so that common components across class and unit flow
 * can be reused
 *
 * For the case of assessment we are returning as it is
 * For attachment we are taking out attachment to make it more flatter
 */

const getUpdatedFeedData = ({ feedData }) => {
  return _.map(feedData, node => {
    const item = _.get(node, "item", {});
    const isUnitResource = _.has(item, "attachmentType");
    const hasAttachment = _.has(item, "attachment") || isUnitResource;
    const attachment = isUnitResource ? item : _.get(item, "attachment", {});

    const updatedAttachment = hasAttachment
      ? {
          ...attachment,
          type: attachment?.attachmentType,
          title: attachment?.attachmentTitle,
        }
      : null;

    const resourceType = _.get(node, "resourceType", "");

    return {
      ...node,
      attachment: updatedAttachment,
      resourceType,
    };
  });
};

export const getUpdatedFeedDataMemoized = getMemoizedFunction(
  getUpdatedFeedData
);

export const getCombinedLibraryFeed = data =>
  _.get(data, "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed", []);

export const getCombinedLibraryFeedEdges = feedData =>
  _.get(
    feedData,
    "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
    []
  );

export const getCombinedLibraryFeedTotalCount = feedData =>
  _.get(
    feedData,
    "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.resourceBasedCount.totalCount",
    0
  );

export const customPathForDataInsertion =
  "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed";

export const convertToCombinedLibraryResource = ({ resource }) => {
  const resourceType = _.get(resource, "resourceType", "");
  let item = _.get(resource, "item", {});

  if (resourceType === "ASSESSMENT") {
    item = {
      ...item,
      taggedPractices: [],
    };
  }

  return {
    id: _.get(resource, "id", ""),
    label: _.get(resource, "label", ""),
    displaySequence: _.get(resource, "displaySequence", 0),
    parent: _.get(resource, "parent", null),
    resourceType: _.get(resource, "resourceType", ""),
    item,
    __typename: "LearningCourseFlow",
  };
};

const ASSESSMENT_MENU_ITEMS = [
  {
    label: "common:edit",
    key: "edit",
    icon: <PencilLineOutlined variant="subtle" />,
  },
  {
    label: "common:duplicate",
    key: "duplicate",
    icon: <CopyOutlined variant="subtle" />,
  },
  // {
  //   label: "classRoom:edit_assign_settings",
  //   key: "assign_to_students",
  //   shouldHide: false,
  //   icon: <StudentPendingOutlined variant="subtle" />,
  // },
  {
    label: "common:print",
    key: "print",
    shouldHide: false,
    icon: <PrintOutlined variant="subtle" />,
  },
  {
    key: "CHANGE_TASK",
    label: "common:change_task_type",
    shouldHide: false,
    perm: "FeatureFlag:ChangeTaskType",
    icon: <ReloadArrowOutlined variant="subtle" />,
  },
  {
    label: "common:tag_snp",
    key: "TAG_SNP",
    shouldHide: false,
    icon: <TagOutlined variant="subtle" />,
  },
  {
    label: "common:delete",
    key: "delete",
    isDestructive: true,
    icon: <DeleteOutlined variant="subtle" />,
  },
];

// Function to check custom permissions
const checkCustomPermission = ({ key, customPermissionParams }) => {
  switch (key) {
    case "TAG_SNP": {
      const { totalNewSnpCycles } = customPermissionParams;
      const isModuleEnabled = ACLStorage.getModuleSettingsValue(
        SNPv2_ACCESS_PERMISSION
      );
      return isModuleEnabled && totalNewSnpCycles > 0;
    }
    default:
      return true;
  }
};

const filteredMenuItemsWithLocaleChange = (
  menuItems,
  {
    t,
    curriculumType,
    isWorksheet,
    isGlobalOrCountryUser,
    disableAssignToStudents,
    notCurrentAcademicYear,
    isTermsExists,
    isGradingPeriodUnlocked,
    assessmentType,
    assessmentTypeLabel,
    totalNewSnpCycles,
    isBlueprintCourse,
    isSchoolLibrary = false,
    isEditingDisabled = false,
    isProSchool = false,
  }
) => {
  const menus = [];

  _.forEach(menuItems, element => {
    const { perm, key } = element;

    // Skip print option for worksheets or when screenshot prevention is enabled
    if (
      key === "print" &&
      (isWorksheet || ACLStore.can("FeatureFlag:PreventPrintAndScreenshots"))
    ) {
      return;
    }

    // Skip assign to students for global/country users, blueprint courses, or pro schools
    if (
      key === "assign_to_students" &&
      (isGlobalOrCountryUser || isBlueprintCourse || isProSchool)
    ) {
      return;
    }

    // Skip tag_snp when feature is disabled
    if (
      key === "tag_snp" &&
      ACLStorage.getModuleSettingsValue(SNPv2_ACCESS_PERMISSION) === false
    ) {
      return;
    }

    // Skip change task in PYP curriculum without special permission
    if (
      key === "CHANGE_TASK" &&
      curriculumType === CURRICULUM_TYPE_PYP &&
      !ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP)
    ) {
      return;
    }

    // Check permissions
    const isItemAllowedByPerm = !perm || ACLStore.can(perm, "all");
    const isItemAllowedByCustomPermission = checkCustomPermission({
      key,
      customPermissionParams: { totalNewSnpCycles },
    });

    if (isItemAllowedByPerm && isItemAllowedByCustomPermission) {
      // Handle disabled states and tooltips
      const menuItem = { ...element, label: t(element.label) };

      switch (key) {
        case "edit": {
          menuItem.disabled = isEditingDisabled || !isGradingPeriodUnlocked;
          menuItem.disabledTooltip = !isGradingPeriodUnlocked
            ? t("course:you_cannot_with_label_since_locked", {
                actionLabel: menuItem.label,
              })
            : t("course:locked_item_edit_restriction_tooltip", {
                entity: assessmentTypeLabel,
              });

          if (isEditingDisabled) {
            // Add suffix for disabled items
            menuItem.suffix = <LockFilled variant="subtle" size="xxx-small" />;
          }

          break;
        }

        case "duplicate": {
          menuItem.disabled = !isGradingPeriodUnlocked;
          menuItem.disabledTooltip = t(
            "course:you_cannot_with_label_since_locked",
            {
              actionLabel: menuItem.label,
            }
          );

          break;
        }

        case "CHANGE_TASK": {
          menuItem.disabled =
            assessmentType === ASSESSMENT_TYPE_AI_TUTOR ||
            assessmentType === "worksheet" ||
            isEditingDisabled ||
            !isGradingPeriodUnlocked;
          menuItem.disabledTooltip = isEditingDisabled
            ? t("course:locked_item_edit_restriction_tooltip", {
                entity: assessmentTypeLabel,
              })
            : !isGradingPeriodUnlocked
            ? t("course:you_cannot_with_label_since_locked", {
                actionLabel: menuItem.label,
              })
            : menuItem.disabled
            ? t("classRoom:change_task_type_dropdown_disabled_tooltip")
            : null;

          if (isEditingDisabled) {
            // Add suffix for disabled items
            menuItem.suffix = <LockFilled variant="subtle" size="xxx-small" />;
          }

          break;
        }

        case "assign_to_students": {
          menuItem.disabled =
            notCurrentAcademicYear ||
            !isTermsExists ||
            !isGradingPeriodUnlocked ||
            isSchoolLibrary;

          if (!isGradingPeriodUnlocked) {
            menuItem.disabledTooltip = t(
              "course:you_cannot_with_label_since_locked",
              {
                actionLabel: menuItem.label,
              }
            );
          } else if (!isTermsExists) {
            menuItem.disabledTooltip = t(
              "classRoom:assign_to_students_no_grading_periods_tooltip_with_label_plural",
              {
                label: assessmentTypeLabel || "assessments",
              }
            );
          }

          break;
        }

        case "delete": {
          menuItem.disabled = isEditingDisabled || !isGradingPeriodUnlocked;
          menuItem.disabledTooltip = !isGradingPeriodUnlocked
            ? t("course:you_cannot_with_label_since_locked", {
                actionLabel: menuItem.label,
              })
            : t("course:locked_item_delete_restriction_tooltip", {
                entity: assessmentTypeLabel,
              });

          if (isEditingDisabled) {
            // Add suffix for disabled items
            menuItem.suffix = <LockFilled variant="subtle" size="xxx-small" />;
          }

          break;
        }
      }

      menus.push(menuItem);
    }
  });

  return menus;
};

// Function to get assessment menu items
export const getAssessmentMenuItems = ({
  libraryItem,
  t,
  curriculumType,
  disableAssignToStudents = false,
  notCurrentAcademicYear = false,
  isTermsExists = true,
  isWorksheet = false,
  totalNewSnpCycles = 0,
  isGlobalOrCountryUser = false,
  assessmentType,
  assessmentTypeLabel = "assessments",
  isBlueprintCourse = false,
  isSchoolLibrary = false,
  isEditingDisabled = false,
}) => {
  const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(
    _.get(libraryItem, "item.academicTerm"),
    assessmentType
  );
  const isProSchool = getIsProSchool();

  return filteredMenuItemsWithLocaleChange(ASSESSMENT_MENU_ITEMS, {
    t,
    curriculumType,
    isWorksheet,
    isGlobalOrCountryUser,
    disableAssignToStudents,
    notCurrentAcademicYear,
    isTermsExists,
    isGradingPeriodUnlocked,
    assessmentType,
    assessmentTypeLabel,
    totalNewSnpCycles,
    isBlueprintCourse,
    isSchoolLibrary,
    isEditingDisabled,
    isProSchool,
  });
};
